/**
 * LayerScopeChips — three toggleable segments controlling which layers a
 * formula group applies to. Full-word labels (Paths / Landers / Offers) with
 * an "Applies to" muted prefix for clarity. At least one chip must remain on.
 *
 * For Media Devoted | Created By Ciro Autiero
 */

import { FC } from 'react';
import type { FlowLayer } from '@trafficpulse/shared';

import { cn } from '@/lib/utils';

const ALL_LAYERS: ReadonlyArray<{ id: FlowLayer; label: string }> = [
  { id: 'paths', label: 'Paths' },
  { id: 'landers', label: 'Landers' },
  { id: 'offers', label: 'Offers' },
];

export interface LayerScopeChipsProps {
  value: ReadonlyArray<FlowLayer>;
  onChange: (next: ReadonlyArray<FlowLayer>) => void;
  /** Layers locked off (e.g. already capped by another rule) — not toggleable. */
  disabledLayers?: ReadonlyArray<FlowLayer>;
  /** Layers gated "available soon" — locked, muted, shown with a "· soon" hint. */
  comingSoonLayers?: ReadonlyArray<FlowLayer>;
  /** Drop the "Applies to" prefix — the Rules tab says "On …" in its sentence. */
  hideLabel?: boolean;
}

const LayerScopeChips: FC<LayerScopeChipsProps> = ({
  value,
  onChange,
  disabledLayers = [],
  comingSoonLayers = [],
  hideLabel = false,
}) => {
  const active = new Set(value);
  const locked = new Set(disabledLayers);
  const comingSoon = new Set(comingSoonLayers);
  const toggle = (id: FlowLayer) => {
    if (locked.has(id) || comingSoon.has(id)) return;
    if (active.has(id) && active.size === 1) return;
    const next: FlowLayer[] = ALL_LAYERS
      .map((l) => l.id)
      .filter((id2) => (id2 === id ? !active.has(id2) : active.has(id2)));
    onChange(next);
  };
  return (
    <div className="flex shrink-0 items-center gap-1.5">
      {hideLabel ? null : (
        <span className="text-[10.5px] font-medium uppercase tracking-wide text-muted-foreground">
          Applies to
        </span>
      )}
      <div
        role="group"
        aria-label="Layers this formula applies to"
        className="inline-flex h-7 items-center gap-px rounded border border-border bg-muted/20 p-0.5"
      >
        {ALL_LAYERS.map((layer) => {
          const isActive = active.has(layer.id);
          const isLast = isActive && active.size === 1;
          const isLocked = locked.has(layer.id);
          const isComingSoon = comingSoon.has(layer.id);
          const isDisabled = isLast || isLocked || isComingSoon;
          const title = isComingSoon
            ? `${layer.label} — available soon (rules target Paths for now)`
            : isLocked
              ? `${layer.label} (locked — already capped by another rule)`
              : isLast
                ? `${layer.label} (locked — at least one layer required)`
                : layer.label;
          return (
            <button
              key={layer.id}
              type="button"
              aria-pressed={isActive && !isComingSoon}
              aria-label={layer.label}
              disabled={isDisabled}
              title={title}
              onClick={() => toggle(layer.id)}
              className={cn(
                'inline-flex h-6 items-center rounded-sm px-2 text-[11px] font-medium transition-colors',
                isActive && !isComingSoon
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground',
                isDisabled && 'cursor-not-allowed opacity-90',
                isComingSoon && 'opacity-60',
              )}
            >
              {isComingSoon ? `${layer.label} · soon` : layer.label}
            </button>
          );
        })}
      </div>
    </div>
  );
};

export default LayerScopeChips;
